type BoundaryFile = Record<string, string>;

const BASE = "/boundaries";

const fileCache = new Map<string, Promise<BoundaryFile>>();

function fetchFile(relPath: string): Promise<BoundaryFile> {
  let pending = fileCache.get(relPath);
  if (!pending) {
    pending = fetch(`${BASE}/${relPath}`).then((res) => {
      if (!res.ok) throw new Error(`boundary fetch failed: ${relPath} ${res.status}`);
      return res.json() as Promise<BoundaryFile>;
    });
    pending.catch(() => fileCache.delete(relPath));
    fileCache.set(relPath, pending);
  }
  return pending;
}

export function administrativeLevelForCode(
  code: string,
): "distrito" | "concelho" | "freguesia" {
  if (code.length <= 2) return "distrito";
  if (code.length <= 4) return "concelho";
  return "freguesia";
}

function filesForLevel(level: string, codes: string[]): string[] {
  if (level === "distrito") return ["distrito.json"];
  // Concelho + freguesia are split per district (code's first 2 chars), so we
  // only fetch the in-view district file(s) instead of the whole country.
  if (level === "concelho") {
    const districts = new Set(codes.map((code) => code.slice(0, 2)));
    return [...districts].map((di) => `concelho/${di}.json`);
  }
  if (level === "freguesia") {
    const districts = new Set(codes.map((code) => code.slice(0, 2)));
    return [...districts].map((di) => `freguesia/${di}.json`);
  }
  return [];
}

export async function getBoundaries(
  level: string,
  codes: string[],
): Promise<Map<string, string>> {
  const files = filesForLevel(level, codes);
  const settled = await Promise.allSettled(files.map(fetchFile));

  const merged: BoundaryFile = {};
  for (const result of settled) {
    if (result.status === "fulfilled") Object.assign(merged, result.value);
  }

  const out = new Map<string, string>();
  for (const code of codes) {
    const geometry = merged[code];
    if (geometry) out.set(code, geometry);
  }
  return out;
}

export function _resetBoundaryCache() {
  fileCache.clear();
}
